import CropFreeIcon from '@mui/icons-material/CropFree';
import { Box, CircularProgress, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useDataProvider, useNotify, useRecordContext } from 'react-admin';
import type { DataProvider, RaRecord } from 'react-admin';

type ConfigVar = RaRecord & { name: string; value: string; device: number };

const VAR_NAME = 'BALENA_HOST_CONFIG_disable_overscan';

const DeviceOverscan: React.FC = () => {
  const record = useRecordContext<RaRecord & { id: number }>();
  const dataProvider = useDataProvider<DataProvider>();
  const notify = useNotify();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [configVar, setConfigVar] = React.useState<ConfigVar | null>(null);

  React.useEffect(() => {
    if (!record?.id) return;
    setLoading(true);
    dataProvider
      .getList<ConfigVar>('device config variable', {
        filter: { device: record.id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
      })
      .then(({ data }) => {
        setConfigVar(data.find((v) => v.name === VAR_NAME) ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [record?.id, dataProvider]);

  const overscanDisabled = configVar?.value === '1';

  const handleToggle = React.useCallback(
    async (disableOverscan: boolean) => {
      if (!record?.id) return;
      setSaving(true);
      try {
        if (disableOverscan) {
          if (configVar) {
            const { data } = await dataProvider.update<ConfigVar>('device config variable', {
              id: configVar.id,
              data: { ...configVar, value: '1' },
              previousData: configVar,
            });
            setConfigVar(data);
          } else {
            const { data } = await dataProvider.create<ConfigVar>('device config variable', {
              data: { name: VAR_NAME, value: '1', device: record.id },
            });
            setConfigVar(data);
          }
          notify('Schermranden uitgeschakeld — herstart device om toe te passen', { type: 'success' });
        } else {
          if (configVar) {
            await dataProvider.delete('device config variable', { id: configVar.id, previousData: configVar });
            setConfigVar(null);
          }
          notify('Schermranden ingeschakeld — herstart device om toe te passen', { type: 'info' });
        }
      } catch {
        notify('Kon instelling niet opslaan', { type: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [record?.id, configVar, dataProvider, notify],
  );

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
      <Tooltip title='Schakelt BALENA_HOST_CONFIG_disable_overscan — herstart vereist'>
        <CropFreeIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
      </Tooltip>

      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <FormControlLabel
          control={
            saving ? (
              <CircularProgress size={20} sx={{ mx: '9px' }} />
            ) : (
              <Switch
                checked={overscanDisabled}
                onChange={(e) => void handleToggle(e.target.checked)}
                size='small'
              />
            )
          }
          label={<span style={{ fontSize: '13px' }}>Schermranden uitschakelen</span>}
          sx={{ mr: 0 }}
        />
      )}

      {overscanDisabled && !loading && (
        <Typography variant='caption' sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
          reboot to apply
        </Typography>
      )}
    </Box>
  );
};

export default DeviceOverscan;
